import { Alert } from '@creditornot/cb-alert';
import { Button, IconButton } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { Plus } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useLoggedInUser } from '@creditornot/wolt-auth/react';
import noop from 'lodash/noop';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { useExternalAccessRights } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { usePermissions } from 'common/wolt-permissions';
import { hasAccessRemovalPermission, hasDirectAddUsersPermission } from 'common/wolt-permissions/permissionUtils';

import UserContextMenu from './UserContextMenu';
import UserDeletionModal from './UserDeletionModal';
import { AccessDescription, EntityType, Role } from './types';

type Props = {
  roles: AccessDescription[] | null;
  entityId: string;
  entityType: EntityType;
  errorReason: string | null;
  isRemovingAccess: boolean;
  isRetailContext: boolean;
  onInviteUser: () => void;
  onAddUser: () => void;
  onRemoveAccess: (user: AccessDescription) => Promise<void>;
};

const formatName = (user: AccessDescription): string | undefined => {
  if (user.name === undefined) {
    return undefined;
  }
  return `${user.name.first} ${user.name.last}`;
};

const localizeRole = (role: Role, shouldUseRetailStrings: boolean) => {
  switch (role) {
    case 'brand_owner':
      return <FormattedMessage id="userManagement.brandOwner" />;
    case 'enterprise_owner':
      return <FormattedMessage id="userManagement.enterpriseOwner" />;
    case 'merchant_owner':
      return <FormattedMessage id="userManagement.merchantOwner" />;
    case 'restricted_menu_editor':
      return <FormattedMessage id="userManagement.restrictedMenuEditor" />;
    case 'venue_worker':
      return (
        <FormattedMessage
          id={shouldUseRetailStrings ? 'userManagement.venueWorkerRetail' : 'userManagement.venueWorker'}
        />
      );
    case 'venue_manager':
      return (
        <FormattedMessage
          id={shouldUseRetailStrings ? 'userManagement.venueManagerRetail' : 'userManagement.venueManager'}
        />
      );
    case 'venue_finance_manager':
      return (
        <FormattedMessage
          id={shouldUseRetailStrings ? 'userManagement.storeFinanceManager' : 'userManagement.restaurantFinanceManager'}
        />
      );
    default:
      return null;
  }
};

const Root = styled.div`
  width: 100%;
`;

const NameMissing = styled.span`
  color: ${cbSemanticCssVars.textSecondary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  color: ${cbSemanticCssVars.textPrimary};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const InviteUserButton = styled(IconButton).attrs({
  size: 'small',
  variant: 'grey',
})``;

const AddExistingUserButton = styled(Button).attrs({
  size: 'small',
  variant: 'blue',
})``;

const Actions = styled.div`
  display: flex;

  ${AddExistingUserButton} {
    margin-inline-start: 16px;
  }
`;

const Error = styled(Alert).attrs({
  variant: 'error',
})`
  margin: 26px 0;
`;

const UserManagement = ({
  entityId,
  entityType,
  errorReason,
  isRemovingAccess,
  isRetailContext,
  onAddUser,
  onInviteUser,
  onRemoveAccess,
  roles,
}: Props) => {
  const currentUser = useLoggedInUser();
  const externalAccessRights = useExternalAccessRights();
  const permissions = usePermissions();
  const intl = useIntl();

  const [userToDelete, setUserToDelete] = useState<AccessDescription | undefined>();

  return (
    <Root>
      <Header>
        <T.Heading6>
          <FormattedMessage id="userManagement.title" />
        </T.Heading6>
        <Actions>
          <InviteUserButton onClick={onInviteUser}>
            <Plus />
          </InviteUserButton>
          {hasDirectAddUsersPermission(permissions) && (
            <AddExistingUserButton onClick={onAddUser}>
              <FormattedMessage id="userManagement.directAddUsers" />
            </AddExistingUserButton>
          )}
        </Actions>
      </Header>
      {errorReason && (
        <Error>
          <FormattedMessage id={errorReason} />
        </Error>
      )}
      <DataTable
        dataSource={roles || []}
        getRowKey={record => record.email}
        horizontalPadding="wide"
        loading={roles === null && errorReason === null}
        columns={[
          {
            id: 'name',
            name: intl.formatMessage({ id: 'userManagement.name' }),
            render: ({ record: user }) => {
              return (
                formatName(user) || (
                  <NameMissing>
                    {user.pending ? (
                      <FormattedMessage id="userManagement.invitationSent" />
                    ) : (
                      <FormattedMessage id="userManagement.nameUnavailable" />
                    )}
                  </NameMissing>
                )
              );
            },
          },
          {
            id: 'email',
            name: intl.formatMessage({ id: 'userManagement.email' }),
            render: ({ record: user }) => user.email,
          },
          {
            id: 'role',
            name: intl.formatMessage({ id: 'userManagement.userLevel' }),
            render: ({ record: user }) => localizeRole(user.role, isRetailContext),
          },
          {
            id: 'actions',
            name: 'Actions',
            renderName: false,
            alignContent: 'right',
            render: ({ record: user }) => {
              if (
                !hasAccessRemovalPermission(
                  currentUser.email,
                  user,
                  entityType,
                  entityId,
                  permissions,
                  externalAccessRights,
                )
              ) {
                return null;
              }
              return <UserContextMenu onDelete={() => setUserToDelete(user)} />;
            },
          },
        ]}
      />
      <UserDeletionModal
        isDeleting={isRemovingAccess}
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(undefined)}
        onDelete={() => {
          if (userToDelete) {
            onRemoveAccess(userToDelete)
              .then(() => {
                setUserToDelete(undefined);
              })
              .catch(noop);
          }
        }}
      />
    </Root>
  );
};

export default UserManagement;
